import { Box, Flex, Text } from '@chakra-ui/react';
import { format, startOfWeek, eachDayOfInterval, addDays, isToday } from 'date-fns';
import { PlannerPlan } from '@/lib/types';
import { SCOPE_DOT_COLOR } from '@/pages/planner/plan-scope';

interface WeekStripProps {
  anchor: Date;
  selectedDate: string | null;
  plansByDate: Record<string, PlannerPlan[]>;
  onSelectDate: (date: string) => void;
}

// Week view: a centered row of 7 compact day cells (DOW, day number,
// plan-dots — one per scope in its identity color: violet workspace, brand
// "My Plan"). Cells are borderless by default per the prototype; selected =
// brand fill, today = brand outline with a brand day number (when not
// selected).
export default function WeekStrip({ anchor, selectedDate, plansByDate, onSelectDate }: WeekStripProps) {
  const weekStart = startOfWeek(anchor);
  const days = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) });

  return (
    <Flex justify='center' gap={{ base: 1, sm: 2, md: 4 }}>
      {days.map((day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const isSelected = selectedDate === dateStr;
        const isCurrentDay = isToday(day);
        const dayPlans = plansByDate[dateStr] ?? [];

        return (
          <Box
            key={dateStr}
            as='button'
            type='button'
            onClick={() => onSelectDate(dateStr)}
            display='flex'
            flexDirection='column'
            alignItems='center'
            gap={0.5}
            w={{ base: '42px', md: '52px' }}
            py={2}
            borderRadius='md'
            borderWidth='1px'
            borderColor={isSelected ? 'brand.solid' : isCurrentDay ? 'brand.muted' : 'transparent'}
            bg={isSelected ? 'brand.solid' : 'transparent'}
            cursor='pointer'
            transition='background-color .12s, color .12s, border-color .12s'
            _hover={isSelected ? undefined : { bg: 'bg.subtle' }}
          >
            <Text
              fontSize='xs'
              fontWeight='600'
              textTransform='uppercase'
              color={isSelected ? 'brand.contrast' : 'fg.muted'}
            >
              {format(day, 'EEE')}
            </Text>
            <Text
              fontFamily='heading'
              fontWeight='800'
              fontSize='lg'
              color={isSelected ? 'brand.contrast' : isCurrentDay ? 'brand.solid' : 'fg'}
            >
              {format(day, 'd')}
            </Text>
            <Flex gap='3px' h='6px'>
              {dayPlans.length === 0 ? (
                <Box w='6px' h='6px' />
              ) : (
                dayPlans.map((plan) => (
                  <Box
                    key={plan.scope}
                    w='6px'
                    h='6px'
                    borderRadius='full'
                    bg={isSelected ? 'brand.contrast' : SCOPE_DOT_COLOR[plan.scope]}
                  />
                ))
              )}
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
}
